import { COMPANY_USER_ROLE, RESTAURANT_USER_ROLE } from '@durma-soft/gros-sdk';
import {
  Beef,
  Building,
  Building2,
  Calendar,
  Carrot,
  ClipboardList,
  Clock,
  Cog,
  Group,
  HandPlatter,
  Hotel,
  Megaphone,
  Package,
  PackagePlus,
  QrCodeIcon,
  ReceiptText,
  Sandwich,
  Settings,
  ShoppingBasket,
  Soup,
  TicketCheck,
  Truck,
  UsersRound,
  Utensils,
  UtensilsCrossed,
} from 'lucide-react';

import { TType } from '@/types/general';
import { SidebarItem } from '@/types/sidebar';
import { UnconfirmedProductOfferOrdersBadge } from '@/components/restaurant/product-offers/unconfirmed-product-offer-orders-badge';

const restaurantItems = (t: TType): SidebarItem[] => [
  {
    icon: <Building />,
    link: '/restaurant/menus',
    label: t('sidebar.my-restaurant'),
    subitems: [
      {
        icon: <Utensils />,
        label: t('sidebar.menus'),
        link: '/restaurant/menus',
      },
      {
        icon: <Beef />,
        label: t('sidebar.ingredients'),
        link: '/restaurant/ingredients',
      },
      {
        icon: <Soup />,
        label: t('sidebar.foods'),
        link: '/restaurant/foods',
      },
      {
        icon: <UtensilsCrossed />,
        label: t('sidebar.menu-types'),
        link: '/restaurant/menu-types',
      },
      {
        icon: <UsersRound />,
        label: t('sidebar.restaurant-employees'),
        link: '/restaurant/employees',
      },
      {
        icon: <Hotel />,
        label: t('sidebar.partners'),
        link: '/restaurant/partners',
      },
      {
        icon: <PackagePlus />,
        label: t('sidebar.manual-orders'),
        link: '/restaurant/manual-orders',
      },
      {
        icon: <QrCodeIcon />,
        label: t('sidebar.track-locations'),
        link: '/restaurant/track-locations',
      },
      {
        icon: <ShoppingBasket />,
        label: t('sidebar.product-offers'),
        link: '/restaurant/product-offers',
        hideFrom: [
          RESTAURANT_USER_ROLE.EMPLOYEE,
          RESTAURANT_USER_ROLE.TRACK_EMPLOYEE,
        ],
      },
      {
        icon: <Calendar />,
        label: t('sidebar.meetings'),
        link: '/restaurant/meetings',
        hideFrom: [
          RESTAURANT_USER_ROLE.EMPLOYEE,
          RESTAURANT_USER_ROLE.MANAGER,
          RESTAURANT_USER_ROLE.TRACK_EMPLOYEE,
        ],
        masterOnly: true,
      },
      {
        icon: <Clock />,
        label: t('order-cutoff-times.title'),
        link: '/restaurant/order-cutoff-times',
      },
      {
        icon: <Settings />,
        label: t('sidebar.settings'),
        link: '/restaurant/settings',
        hideFrom: [RESTAURANT_USER_ROLE.EMPLOYEE],
      },
    ],
    hideFrom: [RESTAURANT_USER_ROLE.TRACK_EMPLOYEE],
  },
  {
    icon: <ClipboardList />,
    label: t('sidebar.reports'),
    link: '/restaurant/reports/ingredients',
    subitems: [
      {
        icon: <Beef />,
        label: t('sidebar.report-ingredients'),
        link: '/restaurant/reports/ingredients',
      },
      {
        icon: <Carrot />,
        label: t('sidebar.report-ingredients-by-location'),
        link: '/restaurant/reports/ingredients-by-location',
      },
      {
        icon: <Soup />,
        label: t('sidebar.report-foods'),
        link: '/restaurant/reports/foods',
      },
      {
        icon: <TicketCheck />,
        label: t('sidebar.orders'),
        link: '/restaurant/reports/orders',
      },
      {
        icon: <Sandwich />,
        label: t('sidebar.orders-by-food'),
        link: '/restaurant/reports/orders-by-food',
      },
      {
        icon: <Package />,
        label: t('sidebar.package'),
        link: '/restaurant/reports/packages',
      },
      {
        icon: <UsersRound />,
        label: t('sidebar.restaurant-customers'),
        link: '/restaurant/reports/restaurant-customer-orders',
      },
      {
        icon: <Sandwich />,
        label: t('sidebar.substitute-meals'),
        link: '/restaurant/reports/substitute-meals',
      },
      {
        icon: <HandPlatter />,
        label: t('sidebar.restaurants'),
        link: '/restaurant/reports/restaurants',
        hideFrom: [RESTAURANT_USER_ROLE.MANAGER, RESTAURANT_USER_ROLE.EMPLOYEE],
      },
      {
        icon: <ReceiptText />,
        label: t('sidebar.invoicing'),
        link: '/restaurant/reports/invoicing',
        hideFrom: [RESTAURANT_USER_ROLE.MANAGER, RESTAURANT_USER_ROLE.EMPLOYEE],
      },
    ],
    hideFrom: [RESTAURANT_USER_ROLE.TRACK_EMPLOYEE],
  },
  {
    icon: <Building2 />,
    label: t('sidebar.companies'),
    link: '/restaurant/companies',
    hideFrom: [RESTAURANT_USER_ROLE.TRACK_EMPLOYEE],
    subitems: [
      {
        icon: <Group />,
        label: t('sidebar.groups'),
        link: '/restaurant/companies/groups',
        hideFrom: [RESTAURANT_USER_ROLE.TRACK_EMPLOYEE],
      },
    ],
  },
  {
    icon: <Cog />,
    link: '/restaurant/platform/product-offers',
    label: t('sidebar.platform-settings'),
    masterOnly: true,
    hideFrom: [
      RESTAURANT_USER_ROLE.EMPLOYEE,
      RESTAURANT_USER_ROLE.TRACK_EMPLOYEE,
      RESTAURANT_USER_ROLE.MANAGER,
    ],
    subitems: [
      {
        icon: <Megaphone />,
        label: t('sidebar.ads'),
        link: '/restaurant/ads',
      },
      {
        icon: <ShoppingBasket />,
        label: t('sidebar.product-offers'),
        link: '/restaurant/platform/product-offers',
      },
      {
        icon: <Truck />,
        label: t('sidebar.suppliers'),
        link: '/restaurant/platform/suppliers',
      },
      {
        icon: <ClipboardList />,
        label: t('sidebar.order-overview'),
        link: '/restaurant/platform/orders',
        badge: <UnconfirmedProductOfferOrdersBadge />,
      },
    ],
  },
  {
    icon: <QrCodeIcon />,
    label: t('sidebar.track'),
    link: '/restaurant/track',
    hideFrom: [
      RESTAURANT_USER_ROLE.OWNER,
      RESTAURANT_USER_ROLE.MANAGER,
      RESTAURANT_USER_ROLE.EMPLOYEE,
    ],
  },
];

const companyItems = (t: TType): SidebarItem[] => [
  {
    icon: <Building />,
    link: '/company/locations',
    label: t('sidebar.my-company'),
    hideFrom: [COMPANY_USER_ROLE.TRACK_EMPLOYEE, COMPANY_USER_ROLE.EMPLOYEE],
    subitems: [
      {
        icon: <Building2 />,
        label: t('sidebar.locations'),
        link: '/company/locations',
      },
      {
        icon: <UsersRound />,
        label: t('sidebar.employees'),
        link: '/company/employees',
      },
      {
        icon: <QrCodeIcon />,
        label: t('sidebar.track-locations'),
        link: '/company/track-locations',
      },
    ],
  },
  {
    icon: <ClipboardList />,
    label: t('sidebar.reports'),
    link: '/company/reports/foods',
    hideFrom: [COMPANY_USER_ROLE.TRACK_EMPLOYEE, COMPANY_USER_ROLE.EMPLOYEE],
    subitems: [
      {
        icon: <Soup />,
        label: t('sidebar.report-foods'),
        link: '/company/reports/foods',
      },
      {
        icon: <UsersRound />,
        label: t('sidebar.restaurant-customers'),
        link: '/company/reports/customer-orders',
      },
      {
        icon: <TicketCheck />,
        label: t('sidebar.orders'),
        link: '/company/reports/orders',
      },
      {
        icon: <Sandwich />,
        label: t('sidebar.orders-by-food'),
        link: '/company/reports/orders-by-food',
      },
    ],
  },
  {
    icon: <QrCodeIcon />,
    label: t('sidebar.track'),
    link: '/company/track',
    hideFrom: [
      COMPANY_USER_ROLE.OWNER,
      COMPANY_USER_ROLE.MANAGER,
      COMPANY_USER_ROLE.EMPLOYEE,
    ],
  },
];

export const config = (t: TType) => {
  return {
    restaurantItems: restaurantItems(t),
    companyItems: companyItems(t),
  };
};
